"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useSession } from "next-auth/react";

interface AnalyzeResult {
  url: string;
  finalUrl: string;
  redirects: { from: string; to: string | null; statusCode: number; statusText: string }[];
  usedPuppeteer?: boolean;
  certificate: Record<string, unknown> | null;
  serverIp: string;
  nsRecords: string[];
  responseHeaders: Record<string, string>;
  headersSource: string | null;
  headersError: string | null;
  canonical: string;
  metaTitle: string | null;
  metaDescription: string | null;
  logo: string | null;
  logoFallback: string | null;
  topColors: { color: string; count: number }[];
  linksInternal: number;
  linksExternal: number;
  linksInternalFollow?: number;
  linksInternalNofollow?: number;
  linksExternalFollow?: number;
  linksExternalNofollow?: number;
}

const META_OK_MAX_TITLE = 50;
const META_OK_MAX_DESC = 150;

export function InfodominiClient({ initialDomain }: { initialDomain?: string }) {
  const { data: session, status } = useSession();
  const [domain, setDomain] = useState(initialDomain ?? "");
  const [userAgent, setUserAgent] = useState("");
  const [referer, setReferer] = useState("same");
  const [advanced, setAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<AnalyzeResult | null>(null);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (initialDomain && !submittedRef.current) {
      submittedRef.current = true;
      setDomain(initialDomain);
      runAnalysis(initialDomain, userAgent || undefined, referer || undefined);
    }
  }, [initialDomain]);

  async function runAnalysis(url: string, ua?: string, ref?: string) {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await fetch("/api/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ url: url.startsWith("http") ? url : `https://${url}`, userAgent: ua, referer: ref }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Error en l'anàlisi");
        return;
      }
      setResult(data);
      const domainForUrl = url.replace(/^https?:\/\//, "").split("/")[0];
      if (typeof window !== "undefined") {
        window.history.replaceState(null, "", `/infodomini/@${domainForUrl}`);
      }
    } catch (e) {
      setError((e as Error).message ?? "Error de connexió");
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const url = domain.trim() ? (domain.startsWith("http") ? domain : `https://${domain}`) : "";
    if (!url) {
      setError("Introduïu un domini o URL");
      return;
    }
    runAnalysis(url, userAgent || undefined, referer || undefined);
  }

  const titleLen = result?.metaTitle?.length ?? 0;
  const descLen = result?.metaDescription?.length ?? 0;
  const titleOk = result?.metaTitle != null && titleLen > 0 && titleLen <= META_OK_MAX_TITLE;
  const titleKo = result?.metaTitle != null && (titleLen === 0 || titleLen > META_OK_MAX_TITLE);
  const descOk = result?.metaDescription != null && descLen > 0 && descLen <= META_OK_MAX_DESC;
  const descKo = result?.metaDescription != null && (descLen === 0 || descLen > META_OK_MAX_DESC);
  const has403 = result?.usedPuppeteer === true;
  const noColors = has403;

  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      <div className="max-w-[1100px] mx-auto px-4 py-6">
        <header className="bg-white rounded-xl shadow-sm p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔍</span>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Info Domini</h1>
              <p className="text-sm text-gray-500">Analitza qualsevol lloc web i extreu la seva informació</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {status === "authenticated" ? (
              <>
                <span className="text-sm text-gray-600">{session?.user?.email}</span>
                <Link href="/superadmin" className="text-sm text-red-600 hover:underline">Panell</Link>
                <Link href="/api/auth/signout" className="text-sm text-gray-500 hover:underline">Sortir</Link>
              </>
            ) : (
              <Link href="/login" className="text-sm text-red-600 hover:underline">Iniciar sessió</Link>
            )}
          </div>
        </header>

        <section className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="domain" className="block text-sm font-medium text-gray-700 mb-1">URL del lloc web</label>
              <input
                id="domain"
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="exemple.com o https://exemple.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setAdvanced(!advanced)}>
              <input type="checkbox" id="advanced" checked={advanced} onChange={() => setAdvanced(!advanced)} />
              <label htmlFor="advanced" className="text-sm text-gray-600">Mode avançat (User-Agent, Referer)</label>
            </div>
            {advanced && (
              <div className="space-y-4 pt-2 border-t border-gray-200">
                <div>
                  <label htmlFor="userAgent" className="block text-sm font-medium text-gray-700 mb-1">User-Agent</label>
                  <select
                    id="userAgent"
                    value={userAgent}
                    onChange={(e) => setUserAgent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Per defecte</option>
                    <option value="Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36">Chrome (Windows)</option>
                    <option value="Mozilla/5.0 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)">Googlebot</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="referer" className="block text-sm font-medium text-gray-700 mb-1">Referer</label>
                  <select id="referer" value={referer} onChange={(e) => setReferer(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="">Cap</option>
                    <option value="google">Google</option>
                    <option value="bing">Bing</option>
                    <option value="same">Mateix domini</option>
                  </select>
                </div>
              </div>
            )}
            <button type="submit" disabled={loading} className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50">
              {loading ? "Analitzant..." : "Analitzar Domini"}
            </button>
          </form>
          {loading && <p className="mt-2 text-sm text-gray-500">Analitzant el lloc web...</p>}
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </section>

        {result && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Resultats de l&apos;anàlisi</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2"><span>↪️</span><span className="font-medium">Redireccions</span></div>
                <div className="text-sm text-gray-700">
                  {result.redirects?.length
                    ? result.redirects.map((r, i) => (
                        <div key={i} className="py-1">{r.from} → {r.to ?? "-"} ({r.statusCode})</div>
                      ))
                    : "-"}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2"><span>🔗</span><span className="font-medium">URL canònica</span></div>
                <div className="text-sm text-gray-700 break-all">{result.canonical ?? "-"}</div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 md:col-span-2">
                <div className="flex items-center gap-2 mb-2"><span>📝</span><span className="font-medium">Meta title</span></div>
                <p className="text-xs text-gray-500 mb-1">Màxim 50 caràcters recomanats</p>
                <div className={`text-sm ${titleOk ? "text-green-700" : titleKo ? "text-red-600 meta-ko" : "text-gray-700"}`}>
                  {result.metaTitle ?? <span className="text-red-600">No disponible{has403 ? " (possible error 403)" : ""}</span>}
                  {result.metaTitle != null && result.metaTitle !== "" && <span className="ml-2 text-gray-400">({titleLen} car.)</span>}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 md:col-span-2">
                <div className="flex items-center gap-2 mb-2"><span>📄</span><span className="font-medium">Meta descripció</span></div>
                <p className="text-xs text-gray-500 mb-1">Màxim 150 caràcters recomanats</p>
                <div className={`text-sm ${descOk ? "text-green-700" : descKo ? "text-red-600 meta-ko" : "text-gray-700"}`}>
                  {result.metaDescription ?? <span className="text-red-600">No disponible{has403 ? " (possible error 403)" : ""}</span>}
                  {result.metaDescription != null && result.metaDescription !== "" && <span className="ml-2 text-gray-400">({descLen} car.)</span>}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2"><span>🔀</span><span className="font-medium">Enllaços interns</span></div>
                <div className="text-sm text-gray-700">{result.linksInternal ?? 0}</div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2"><span>🔀</span><span className="font-medium">Enllaços externs</span></div>
                <div className="text-sm text-gray-700">{result.linksExternal ?? 0}</div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2"><span>🔒</span><span className="font-medium">Certificat SSL</span></div>
                <div className="text-sm text-gray-700">
                  {result.certificate ? (
                    <span className="text-green-700">Vàlid</span>
                  ) : (
                    <span className="text-red-600">No disponible</span>
                  )}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2"><span>📋</span><span className="font-medium">Capçaleres HTTP</span></div>
                <div className="text-sm text-gray-700 max-h-40 overflow-y-auto">
                  {result.responseHeaders && Object.keys(result.responseHeaders).length > 0
                    ? Object.entries(result.responseHeaders).map(([k, v]) => (
                        <div key={k} className="py-0.5 border-b border-gray-100"><strong>{k}:</strong> {String(v).slice(0, 80)}</div>
                      ))
                    : <span className="text-red-600">No disponible</span>}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2"><span>🌐</span><span className="font-medium">IP del servidor</span></div>
                <div className="text-sm text-gray-700 font-mono">{result.serverIp ?? "-"}</div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2"><span>📡</span><span className="font-medium">Registres NS</span></div>
                <div className="text-sm text-gray-700">
                  {result.nsRecords?.length ? result.nsRecords.map((ns, i) => <div key={i} className="font-mono">{ns}</div>) : "-"}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2"><span>🖼️</span><span className="font-medium">Logo</span></div>
                <div className="text-sm">
                  {(result.logo ?? result.logoFallback) ? (
                    <img src={result.logo ?? result.logoFallback ?? ""} alt="Logo" className="h-10 w-auto object-contain" />
                  ) : (
                    <span className="text-red-600">No disponible</span>
                  )}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2"><span>🎨</span><span className="font-medium">Colors</span></div>
                <div className="text-sm">
                  {noColors ? (
                    <span className="text-red-600">No disponible (error 403)</span>
                  ) : result.topColors?.length ? (
                    <div className="flex flex-wrap gap-2">
                      {result.topColors.map((c, i) => (
                        <span key={i} className="inline-flex items-center gap-1">
                          <span className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: c.color }} />
                          <span className="font-mono text-xs">{c.color}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-red-600">No disponible</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
